<script setup>
import { ref, watch, computed } from 'vue';
import { useRouter } from 'vue-router';
import { useEmployeeStore } from '@/stores/employee.js';
import Column from "primevue/column";
import DataTable from "primevue/datatable";
import Avatar from "primevue/avatar";
import { useServerTable } from '@/composables/useServerTable.js';


const props = defineProps({
  search: { type: String, default: '' },
  isActive: { type: Boolean, required: false, default: undefined }
});

const emit = defineEmits(['initial-count-loaded']);
const initialLoadEmitted = ref(false);


const employeeStore = useEmployeeStore();
const router = useRouter();

const apiOrigin = computed(() => {
  try {
    const u = new URL(import.meta.env.VITE_API_BASE_URL, window.location.origin);
    return u.origin;
  } catch {
    return window.location.origin;
  }
});

const fileUrl = (path) => {
  if (!path) return null;
  if (/^https?:\/\//i.test(path)) return path;
  return `${apiOrigin.value}${path.startsWith('/') ? '' : '/'}${path}`;
};

const getInitials = (firstName, lastName) => {
  return ((firstName?.[0] || '') + (lastName?.[0] || '')).toUpperCase() || '?';
};

const {
  data: employees,
  totalRecords,
  loading,
  rowsPerPage,
  currentPage,
  setParams,
  load,
  setSearch,
  setExtra,
  onPageChange,
  firstIndex
} = useServerTable({
  initialRows: 10,
  debounceMs: 800,
  storageKey: 'table.employees.pageSize',
  loadPage: async ({ page, rows, search, ...extra }) => {
    return await employeeStore.loadEmployees({
      page,
      page_size: rows,
      search,
      is_active: typeof extra.is_active === 'boolean' ? extra.is_active : undefined
    });
  }
});

const goToEmployee = (event) => {
  if (event.data && event.data.id) {
    router.push(`/employees/${event.data.id}`);
  }
};

watch(
    () => [props.search, props.isActive],
    (newVals, oldVals) => {
      const isInitialLoad = oldVals === undefined;
      const [search, isActive] = newVals || [];

      if (!isInitialLoad) {
        const [prevSearch, prevIsActive] = oldVals || [];
        if (search === prevSearch && isActive === prevIsActive) {
          return;
        }
      }

      setParams({
        search: (search || '').trim(),
        extra: { is_active: typeof isActive === 'boolean' ? isActive : undefined },
        resetPage: true,
        immediate: isInitialLoad,
      });
    },
    { immediate: true }
);

// Эмитим count после первой загрузки
watch(totalRecords, (count) => {
  if (!initialLoadEmitted.value && count !== null) {
    initialLoadEmitted.value = true;
    emit('initial-count-loaded', { count, isActive: props.isActive });
  }
});

</script>


<template>
  <DataTable :value="employees"
             stripedRows
             lazy
             paginator
             scrollable
             scrollHeight="flex"
             :rows="rowsPerPage"
             :rowsPerPageOptions="[10, 20, 30]"
             :totalRecords="totalRecords"
             :loading="loading"
             :first="firstIndex()"
             @page="onPageChange"
             @row-click="goToEmployee"
             class="cursor-pointer"
  >
    <Column
        header="ФИО"
        :headerClass="'w-1/2 lg:w-[40%]'"
        :bodyClass="'w-1/2 lg:w-[40%]'"
    >
      <template #body="slotProps">
        <div style="display: flex; align-items: center;">
          <Avatar
              v-if="fileUrl(slotProps.data.avatar)"
              :image="fileUrl(slotProps.data.avatar)"
              shape="circle"
              size="small"
              style="margin-right: 8px;"
          />
          <Avatar
              v-else
              :label="getInitials(slotProps.data.first_name, slotProps.data.last_name)"
              shape="circle"
              size="small"
              style="margin-right: 8px; background-color: #9ca3af; color: white;"
          />
          <span>{{ [slotProps.data.last_name, slotProps.data.first_name].filter(Boolean).join(' ') }}</span>
        </div>
      </template>
    </Column>

    <Column
        field="facility_count"
        header="Объекты"
        style="width: 5%"
        :headerClass="'hidden lg:table-cell'"
        :bodyClass="'hidden lg:table-cell'"
    ></Column>
    <Column
        field="work_count"
        header="Работы"
        style="width: 5%"
        :headerClass="'hidden lg:table-cell'"
        :bodyClass="'hidden lg:table-cell'"
    ></Column>
    <Column
        field="department"
        header="Отдел"
        style="width: 25%"
        :headerClass="'hidden lg:table-cell'"
        :bodyClass="'hidden lg:table-cell'"
    ></Column>

    <Column
        field="post"
        header="Должность"
        :headerClass="'w-1/2 lg:w-[25%]'"
        :bodyClass="'w-1/2 lg:w-[25%]'"
    ></Column>

  </DataTable>
</template>

<style scoped />
